// The passenger card's STATUS BAND — the pure half. ONE sentence of where the PNR is and
// ONE primary action (at most one secondary), derived from the same next-up the steps use
// plus, while Nexa's purchase of the flight is in trouble, its recovery (§6.6). The band
// never contradicts the state: a refused purchase reads as refused, never as issuing.
//
// i18n: keys only (`pnr:band.*`, `pnr:recovery.*`); the translator is handed in.

import type { IRecoveryView } from '@/lib/recovery/types';
import type { IOfferExpiry } from '@/lib/rebooking/offer-expiry';
import {
  bestCandidate,
  failureClause,
  failureReasonOf,
  formatDelay,
  offerable,
  providerName,
  type PurchasePhase,
} from '@/lib/recovery/recovery-model';
import type { BoardTone } from './airport-tokens';
import type { NextUp, PnrT, StepKey } from './pnr-steps';

/** The card's views: the card itself, one per service, the activity and the replacement chooser. */
export type CardView = 'overview' | StepKey | 'activity' | 'replacement';

export type BandActionKind =
  | 'select'
  | 'generate'
  | 'ticket'
  | 'hotel'
  | 'resend'
  | 'forceAccept'
  | 'view'
  | 'recoverySearch'
  | 'recoveryOffer'
  | 'recoveryReconcile'
  | 'resendTicket'
  | 'call'
  | 'contact'
  /** The offer expired (its flight, or every option, left): a new search — the card picks how. */
  | 'searchFresh';

export interface BandAction {
  kind: BandActionKind;
  label: string;
  /** `view`: which view it opens. */
  view?: CardView;
  /** `recoveryOffer`: the candidate it offers. */
  optionUrn?: string;
}

export interface StatusBand {
  /** Stable machine kind (`data-kind`) — the next-up kind, `purchase-<phase>` or `external-*`. */
  kind: string;
  tone: BoardTone;
  /** The band's section title (drawn in capitals by CSS, written in sentence case). */
  kicker: string;
  title: string;
  body?: string | undefined;
  primary?: BandAction | undefined;
  secondary?: BandAction | undefined;
  /** Waiting on the read that says it: drawn as a neutral loading state, no claim, no action. */
  loading?: boolean | undefined;
}

export interface BandRecoveryInput {
  phase: PurchasePhase;
  view?: IRecoveryView | undefined;
  /** `duffel` / `atlas`. */
  provider?: string | undefined;
  /** The row's `nexaPurchase.recovery.reason` (NEEDS_OPERATOR). */
  reason?: string | undefined;
  /** The row's `nexaPurchase.recovery.acceptBy` (REOFFERED). */
  acceptBy?: string | undefined;
  /** The row's count of options the latest search left — speaks until the view is read. */
  rowCandidates?: number | undefined;
  /** The per-group view is being read / could not be read. */
  viewLoading?: boolean | undefined;
  viewFailed?: boolean | undefined;
  /** A refused purchase that predates recovery: the reason line, already worded. */
  reviewWhy?: string | undefined;
  /** The legacy refusal reason code (`PURCHASE_UNCONFIRMED`, `MISSING_DATA`, …). */
  reviewReason?: string | undefined;
  /** The flight the passenger accepted, as a label («ZZ7426»). */
  acceptedFlight?: string | undefined;
  /** The re-offered flight the passenger accepted since (`reacceptedCandidate`): its purchase is
   *  the one that needs the operator now — never offer it to them again. */
  reaccepted?: { flightNumber: string; departAt: string } | undefined;
  /** Why the purchase was refused, already worded from the row (`Faltan datos de 2 pasajeros…`). */
  refusalWhy?: string | undefined;
}

export interface BandInput {
  nextUp: NextUp;
  recovery?: BandRecoveryInput | undefined;
  /** ISO → «02:30» at the right airport. */
  formatTime: (iso: string) => string;
  /** Ticketed flight, for «todo en orden». */
  ticketed?: { flight?: string | undefined; when?: string | undefined } | undefined;
  /** Legacy select / generate / issue writes are available on this deployment. */
  legacyActions: boolean;
  /** The hotel can be allocated in one click (a routing disruption exists). */
  hotelAllocatable: boolean;
  /** A notification row with an offer exists: «Reenviar la oferta» means something. */
  canResend: boolean;
  /** The offer awaits the passenger: a supervisor may force the acceptance. */
  awaiting: boolean;
  /** A phone may be on file — «Llamar» works; else the contact is fixed first. */
  canCall?: boolean | undefined;
  /** The passenger's answered decline asked for a call (and no newer offer awaits them). */
  callback?: boolean | undefined;
  /** The clock a candidate's departure is judged against (default: now). */
  now?: number | undefined;
  /** The offer's flight — or every option — already left with no ticket (`offerExpiryOf`). */
  expired?: IOfferExpiry | null | undefined;
}

/** Legacy refusals the operator fixes elsewhere (data, price, policy) — a new search won't. */
const REVIEW_OPENS_FLIGHT = new Set([
  'AUTO_PURCHASE_DISABLED',
  'POLICY_UNAVAILABLE',
  'PRICE_CARD_UNAVAILABLE',
  'OVER_PRICE_CAP',
  'PRICE_NOT_COMPARABLE',
  'MISSING_DATA',
  'PASSPORT_REQUIRED',
  'LIVE_BOOKING_DISABLED',
  'SPECIAL_ASSISTANCE',
  'TICKET_NUMBERS_PENDING',
]);

const KICKER: Record<NextUp['kind'], string> = {
  purchase: 'band.kicker.needsDecision',
  noOptions: 'band.kicker.needsDecision',
  generate: 'band.kicker.needsDecision',
  select: 'band.kicker.needsDecision',
  hotel: 'band.kicker.needsDecision',
  acceptance: 'band.kicker.waitingPassenger',
  ticket: 'band.kicker.ticketMissing',
  card: 'band.kicker.cardMissing',
  transport: 'band.kicker.transportMissing',
  done: 'band.kicker.allInOrder',
};

function recoveryBand(i: BandInput, r: BandRecoveryInput, t: PnrT): StatusBand {
  const provider = providerName(r.provider);
  const view = r.view;
  const kind = `purchase-${r.phase.toLowerCase()}`;
  const flightAt = (f: { flightNumber: string; departAt: string }): string =>
    t('band.flightAt', { flight: f.flightNumber, time: i.formatTime(f.departAt) });

  switch (r.phase) {
    case 'SEARCHING':
      return {
        kind,
        tone: 'info',
        kicker: t('band.kicker.nexaResolving'),
        title: t('band.recovery.searching.title', {
          failure: failureClause(failureReasonOf(view), provider, t, view?.failedAttempt?.field),
        }),
        body: t('band.recovery.searching.body'),
        secondary: { kind: 'view', view: 'flight', label: t('band.action.openFlight') },
      };
    case 'AUTO_BUYING': {
      const best = bestCandidate(view, i.now);
      return {
        kind,
        tone: 'info',
        kicker: t('band.kicker.nexaResolving'),
        title: best?.equivalent
          ? t('band.recovery.autoBuying.title', { flight: flightAt(best) })
          : t('band.recovery.autoBuying.titleNoFlight'),
        body: t('band.recovery.autoBuying.body'),
        secondary: { kind: 'view', view: 'flight', label: t('band.action.openFlight') },
      };
    }
    case 'REOFFERED': {
      const offered = view?.reoffer
        ? view.candidates.find((c) => c.optionUrn === view.reoffer?.optionUrn)
        : undefined;
      const acceptBy = view?.reoffer?.acceptBy ?? r.acceptBy;
      return {
        kind,
        tone: 'warn',
        kicker: t('band.kicker.waitingPassenger'),
        title:
          offered && acceptBy
            ? t('band.recovery.reoffered.title', { flight: flightAt(offered), until: i.formatTime(acceptBy) })
            : t('band.recovery.reoffered.titleBare'),
        body: t('band.recovery.reoffered.body'),
        ...(i.canResend ? { primary: { kind: 'resend' as const, label: t('band.action.resendOffer') } } : {}),
        ...(i.awaiting ? { secondary: { kind: 'forceAccept' as const, label: t('band.action.forceAccept') } } : {}),
      };
    }
    case 'CONFIRMING': {
      const u = view?.unconfirmed;
      const flight = view?.accepted?.flightNumber ?? r.acceptedFlight ?? '—';
      return {
        kind,
        tone: 'warn',
        kicker: t('band.kicker.verifying', { provider }),
        title: t('band.recovery.confirming.title', { provider, flight }),
        body:
          u?.giveUpAt && u.nextCheckAt
            ? t('band.recovery.confirming.body', { next: i.formatTime(u.nextCheckAt), giveUp: i.formatTime(u.giveUpAt) })
            : t('band.recovery.confirming.bodyBare'),
        primary: { kind: 'recoveryReconcile', label: t('band.action.verifyNow') },
      };
    }
    case 'RECOVERED':
      return {
        kind,
        tone: 'ok',
        kicker: t('band.kicker.replaced'),
        title: i.ticketed?.flight
          ? t('band.recovery.recovered.title', { flight: i.ticketed.flight, when: i.ticketed.when ?? '—' })
          : t('band.recovery.recovered.titleNoFlight'),
        body: r.acceptedFlight
          ? t('band.recovery.recovered.body', { previous: r.acceptedFlight })
          : undefined,
        primary: { kind: 'resendTicket', label: t('band.action.resendTicket') },
      };
    case 'BUYING':
      return {
        kind,
        tone: 'info',
        kicker: t('band.kicker.buying'),
        title: t('band.recovery.buying.title', { provider }),
        body: t('band.recovery.buying.body'),
        secondary: { kind: 'view', view: 'flight', label: t('band.action.openFlight') },
      };
    case 'REVIEW':
    case 'NEEDS_OPERATOR':
      return decisionBand(i, r, t, kind, provider, flightAt);
  }
}

/** A purchase that needs an operator: why, the best replacement, and the one next step. */
function decisionBand(
  i: BandInput,
  r: BandRecoveryInput,
  t: PnrT,
  kind: string,
  provider: string,
  flightAt: (f: { flightNumber: string; departAt: string }) => string,
): StatusBand {
  const view = r.view;
  const candidates = view?.candidates ?? [];
  const best = bestCandidate(view, i.now);
  const kicker = t('band.kicker.needsDecision');
  // A refusal from before recovery, with no fresh search to show: its own reason, and the
  // step that fixes it (verify, a new search, or the flight's detail for data/price/policy).
  if (r.phase === 'REVIEW' && candidates.length === 0) {
    const reason = r.reviewReason ?? '';
    const primary: BandAction =
      reason === 'PURCHASE_UNCONFIRMED'
        ? { kind: 'recoveryReconcile', label: t('band.action.verifyNow') }
        : REVIEW_OPENS_FLIGHT.has(reason)
          ? { kind: 'view', view: 'flight', label: t('band.action.openFlight') }
          : { kind: 'recoverySearch', label: t('band.action.searchAgain') };
    return {
      kind,
      tone: 'alert',
      kicker,
      title: r.reviewWhy ?? t('band.recovery.review.title'),
      body: t('band.recovery.review.body'),
      primary,
      ...(primary.kind !== 'view'
        ? { secondary: { kind: 'view' as const, view: 'flight' as const, label: t('band.action.openFlight') } }
        : {}),
    };
  }
  // They already accepted the re-offered flight and ITS purchase is what failed: say so, and why —
  // offering them the flight they just accepted made no sense (PO 2026-11-03).
  if (r.reaccepted) {
    return {
      kind,
      tone: 'alert',
      kicker,
      title: t('band.recovery.reaccepted.title', { flight: flightAt(r.reaccepted) }),
      body: r.refusalWhy ?? t('band.recovery.review.body'),
      primary: { kind: 'view', view: 'flight', label: t('band.action.openFlight') },
      secondary: { kind: 'view', view: 'replacement', label: t('band.action.otherWays') },
    };
  }
  // Three refused automatic purchases: options may still exist — the sentence says what to do,
  // never «no options».
  const autoBuyLimit = r.reason === 'AUTO_BUY_LIMIT';
  const failure = autoBuyLimit
    ? t('band.recovery.reason.AUTO_BUY_LIMIT', { provider })
    : r.reason === 'REOFFER_TIMEOUT' || r.reason === 'REOFFER_DECLINED' || r.reason === 'UNCONFIRMED_EXPIRED'
      ? t(`band.recovery.reason.${r.reason}`)
        : failureClause(failureReasonOf(view) ?? r.reviewReason, provider, t, view?.failedAttempt?.field);
  // Until the view is read (or when it cannot be), the row's count speaks: never «no
  // flights» while the latest search left some.
  const rowCount = !view && (r.rowCandidates ?? 0) > 0 ? r.rowCandidates! : 0;
  // Nothing to say yet: no view, no count on the row — wait for the read, claim nothing.
  if (!view && !rowCount && r.viewLoading) {
    return { kind, tone: 'warn', kicker, title: t('band.recovery.loading'), loading: true };
  }
  const outlook = rowCount
    ? t('band.recovery.decision.found', { count: rowCount })
    : best
    ? best.sameAsAccepted
      ? t('band.recovery.decision.sameAsAccepted')
      : best.equivalent
      ? t('band.recovery.decision.equivalent', { flight: flightAt(best) })
      : best.why.includes('OTHER_DAY')
        ? t('band.recovery.decision.otherDay')
        : t('band.recovery.decision.later', { delay: formatDelay(best.arrivalDelayMinutes, t) })
    : candidates.length > 0
      ? t('band.recovery.decision.noneOfferable', { count: candidates.length })
      : view?.searchedAt
      ? t('band.recovery.decision.none', { time: i.formatTime(view.searchedAt) })
      : !view && r.viewFailed
        ? t('band.recovery.decision.unreadable')
        : t('band.recovery.decision.notSearched');
  const body = rowCount
    ? t('band.recovery.decision.bodyOffer')
    : best
    ? best.sameAsAccepted
      ? t('recovery.buy.underAcceptance')
      : best.equivalent
      ? t('band.recovery.decision.bodyEquivalent')
      : r.reason === 'POLICY_OPERATOR'
        ? t('band.recovery.decision.bodyPolicyOperator')
        : t('band.recovery.decision.bodyOffer')
    : t('band.recovery.decision.bodyNone');
  return {
    kind,
    tone: 'warn',
    kicker,
    title: autoBuyLimit ? failure : `${failure} ${outlook}`,
    body,
    primary: best
      ? {
          kind: 'recoveryOffer',
          optionUrn: best.optionUrn,
          // The accepted flight itself: bought under the acceptance already given (RB1).
          label: best.sameAsAccepted
            ? t('band.action.buyAccepted', { flight: flightAt(best) })
            : t('band.action.offer', { flight: flightAt(best) }),
        }
      : rowCount
        ? { kind: 'view', view: 'replacement', label: t('band.action.seeOptions', { count: rowCount }) }
        : { kind: 'recoverySearch', label: t('band.action.searchAgain') },
    secondary:
      candidates.length > 0
        ? { kind: 'view', view: 'replacement', label: t('band.action.seeOptions', { count: candidates.length }) }
        : rowCount
          ? { kind: 'recoverySearch', label: t('band.action.searchAgain') }
          : { kind: 'view', view: 'replacement', label: t('band.action.otherWays') },
  };
}

/** The band for a PNR: the purchase's recovery while there is one, else the next-up. */
/** «Pidió que lo llamen»: the call (or the contact to make it) first; while a recovery needs a
 *  decision, the replacement rides as the secondary. */
function callbackBand(i: BandInput, t: PnrT): StatusBand {
  const r = i.recovery;
  const options = r?.view ? r.view.candidates.filter((c) => offerable(c, i.now)).length : (r?.rowCandidates ?? 0);
  return {
    kind: 'callback',
    tone: 'warn',
    kicker: t('card.talk.callback'),
    title: t('band.callback.title'),
    body: i.canCall === false ? t('card.talk.callbackNoPhone') : t('band.callback.body'),
    primary:
      i.canCall === false
        ? { kind: 'contact', label: t('pnrActions:utility.updateContact') }
        : { kind: 'call', label: t('pnrActions:utility.call.label') },
    secondary: r
      ? options > 0
        ? { kind: 'view', view: 'replacement', label: t('band.action.seeOptions', { count: options }) }
        : { kind: 'recoverySearch', label: t('band.action.searchAgain') }
      : i.canResend
        ? { kind: 'resend', label: t('nextUp.acceptance.primary') }
        : undefined,
  };
}

/**
 * The offer expired (PO 2026-11-03: «Ver vuelo nuevo» opened a flight that had left, with
 * «Emitir ticket» on it — «nunca un callejón sin salida»): which flight left and when, whether
 * they had accepted it, and the ONE way out — a new search. Never «issue» or «choose» it.
 */
function expiredBand(i: BandInput, e: IOfferExpiry, t: PnrT): StatusBand {
  const selected = e.kind === 'selected';
  // Not accepted, the new search is a fresh generation (a legacy write); accepted, it is
  // the recovery's search against what they accepted — available everywhere.
  const canSearch = i.legacyActions || (selected && e.accepted);
  const look: BandAction = selected
    ? { kind: 'view', view: 'flight', label: t('band.action.seeFlight') }
    : { kind: 'view', view: 'flight', label: t('band.action.seeOptionsPlain') };
  return {
    kind: `expired-${e.kind}`,
    tone: 'alert',
    kicker: t('band.kicker.offerExpired'),
    title: selected
      ? t('band.expired.selected.title', { flight: e.flight ?? '—', when: e.departAt ? i.formatTime(e.departAt) : '—' })
      : t('band.expired.options.title'),
    body: selected
      ? t(e.accepted ? 'band.expired.selected.bodyAccepted' : 'band.expired.selected.body')
      : t('band.expired.options.body'),
    ...(canSearch
      ? { primary: { kind: 'searchFresh' as const, label: t('band.action.searchFresh') }, secondary: look }
      : { primary: look }),
  };
}

export function deriveBand(i: BandInput, t: PnrT): StatusBand {
  // An answered decline that asks for a call wins over a recovery's reason (it is the newest
  // word from the passenger) — unless Nexa is already on a new option for them.
  if (i.callback && (!i.recovery || i.recovery.phase === 'NEEDS_OPERATOR' || i.recovery.phase === 'REVIEW')) {
    return callbackBand(i, t);
  }
  // A flight that left, or options that all did, before anything else: a refused purchase of
  // it (missing data…) no longer matters. A recovery already on it keeps its own band.
  if (i.expired && (!i.recovery || i.recovery.phase === 'REVIEW')) return expiredBand(i, i.expired, t);
  if (i.recovery && i.nextUp.kind === 'purchase') return recoveryBand(i, i.recovery, t);
  // Recovered: the replacement is bought. Until its ticket reaches the row the band says so —
  // never «issue the ticket», which would start a second purchase.
  if (i.recovery?.phase === 'RECOVERED' && (i.nextUp.kind === 'done' || i.nextUp.kind === 'ticket')) {
    return recoveryBand(i, i.recovery, t);
  }
  const n = i.nextUp;
  const band: StatusBand = { kind: n.kind, tone: n.tone, kicker: t(KICKER[n.kind]), title: n.title, body: n.body };
  switch (n.kind) {
    case 'select':
      if (i.legacyActions) band.primary = { kind: 'select', label: n.primaryLabel ?? t('nextUp.select.primaryNoFlight') };
      band.secondary = { kind: 'view', view: 'flight', label: t('band.action.seeOptionsPlain') };
      break;
    case 'generate':
      if (i.legacyActions) band.primary = { kind: 'generate', label: t('nextUp.generate.primary') };
      band.secondary = { kind: 'view', view: 'flight', label: t('band.action.openFlight') };
      break;
    case 'noOptions':
      band.kicker = n.tone === 'idle' ? t('band.kicker.noRebooking') : band.kicker;
      band.primary = { kind: 'view', view: 'flight', label: t('band.action.openFlight') };
      break;
    case 'hotel':
      band.primary = i.hotelAllocatable
        ? { kind: 'hotel', label: t('hotel.autoAllocate') }
        : { kind: 'view', view: 'hotel', label: t('band.action.chooseHotel') };
      if (i.hotelAllocatable) band.secondary = { kind: 'view', view: 'hotel', label: t('band.action.chooseHotel') };
      break;
    case 'acceptance':
      if (n.cause === 'callback') return callbackBand(i, t);
      if (n.cause === 'deliveryFailed') band.kicker = t('band.kicker.notReached');
      if (n.cause === 'declined') band.kicker = t('band.kicker.declined');
      if (i.canResend) band.primary = { kind: 'resend', label: t('nextUp.acceptance.primary') };
      band.secondary = i.awaiting
        ? { kind: 'forceAccept', label: t('band.action.forceAccept') }
        : { kind: 'view', view: 'passenger', label: t('band.action.openConversation') };
      break;
    case 'ticket':
      if (i.legacyActions) band.primary = { kind: 'ticket', label: t('nextUp.ticket.primary') };
      else band.primary = { kind: 'view', view: 'flight', label: t('band.action.openFlight') };
      break;
    case 'card':
      band.primary = { kind: 'view', view: 'meals', label: t('nextUp.card.primary') };
      break;
    case 'transport':
      band.primary = { kind: 'view', view: 'transport', label: t('nextUp.transport.primary') };
      break;
    case 'done':
      if (i.ticketed?.flight) {
        band.title = t('band.done.titleTicketed', { flight: i.ticketed.flight, when: i.ticketed.when ?? '—' });
        band.primary = { kind: 'resendTicket', label: t('band.action.resendTicket') };
      }
      break;
    case 'purchase':
      // A purchase phase without its recovery input (the drawer always passes one).
      break;
  }
  return band;
}
